// ─────────────────────────────────────────────────────────────────────────────
// SubscriptionGate
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from "react";
import { C } from "../../lib/colors";
import { SUPABASE_URL, authFetch } from "../../lib/supabase";

// Single plan: $15/month (Oct 2026). Annual plan discontinued.
const MONTHLY_PRICE_ID = "price_1UM5ssL8HSUTWmPdohLoAS64";

export function isSubscribed(profile) {
  if (!profile) return false;
  if (profile.is_gifted === true) return true;
  if (profile.subscription_status === "active" ||
      profile.subscription_status === "trialing") return true;
  // Past Due: Stripe is retrying the payment (up to 8 retries over 2 weeks
  // per our Stripe Revenue Recovery config). User keeps access during the
  // retry window — locking them out on the first failed charge would punish
  // legitimate users (expired cards, fraud holds, network blips) and create
  // support load. If retries ultimately fail, Stripe fires
  // customer.subscription.deleted and status flips to "canceled" via the
  // webhook, at which point access is cut by the clause below.
  if (profile.subscription_status === "past_due") return true;
  // Canceled but still within paid period — keep access until end date
  if (profile.subscription_status === "canceled" &&
      profile.subscription_end &&
      new Date(profile.subscription_end) > new Date()) return true;
  return false;
}

async function startCheckout(priceId) {
  const res = await authFetch(`${SUPABASE_URL}/functions/v1/stripe-checkout`, {
    method: "POST",
    body: JSON.stringify({
      priceId,
      successUrl: `${window.location.origin}/?subscription=success`,
      cancelUrl:  `${window.location.origin}/`,
    }),
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data.url;
}

export function SubscriptionGate({ profile, onUpgrade, children, feature = "This feature" }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  if (isSubscribed(profile)) return children;

  const handleCheckout = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = await startCheckout(MONTHLY_PRICE_ID);
      window.location.href = url;
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  // New user — never had a subscription. Show a single trial CTA, not a paywall.
  const isNewUser = !profile?.subscription_status || profile?.subscription_status === "inactive";

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "60px 24px",
      textAlign: "center",
      minHeight: 400,
    }}>
      {/* Lock icon */}
      <div style={{
        width: 64, height: 64,
        borderRadius: "50%",
        background: C.purple + "20",
        border: `2px solid ${C.purple}40`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 28, marginBottom: 20,
      }}>
        {isNewUser ? "🚀" : "🔒"}
      </div>

      <h2 style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 800, color: C.text }}>
        {isNewUser ? "Start your free 30-day trial" : `${feature} requires Futures OS Pro`}
      </h2>

      <p style={{
        color: C.muted, fontSize: 14, maxWidth: 420,
        lineHeight: 1.7, margin: "0 0 8px",
      }}>
        {isNewUser
          ? "Get full access to all 22 behavioral detectors, AI coaching insights, and your prop firm dashboard — free for 30 days."
          : "Unlock all 22 behavioral detectors, AI coaching insights, pattern history, and the full analytics suite."}
      </p>

      {isNewUser ? (
        <>
          <p style={{ color: C.muted, fontSize: 13, margin: "0 0 28px" }}>
            No credit card required to start. Cancel anytime.
          </p>
          <button
            onClick={handleCheckout}
            disabled={loading}
            style={{
              padding: "14px 40px",
              background: C.purple,
              border: "none",
              borderRadius: 10,
              color: "#fff",
              fontSize: 15,
              fontWeight: 700,
              cursor: loading ? "default" : "pointer",
              marginBottom: 16,
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Loading…" : "Start my free trial →"}
          </button>
          <p style={{ fontSize: 11, color: C.muted }}>
            Then $15/month — cancel anytime.
          </p>
        </>
      ) : (
        <>
          <p style={{ color: C.muted, fontSize: 13, margin: "0 0 28px" }}>
            30-day free trial — cancel anytime.
          </p>
          <div style={{
            width: "100%",
            maxWidth: 200,
            marginBottom: 16,
          }}>
            <PriceCard
              label="Monthly"
              price="$15"
              per="/ month"
              note="Billed monthly"
              loading={loading}
              disabled={loading}
              onClick={handleCheckout}
            />
          </div>
        </>
      )}

      {error && (
        <div style={{
          marginTop: 8, padding: "8px 12px",
          background: C.red + "10", border: `1px solid ${C.red}30`,
          borderRadius: 6, fontSize: 12, color: C.red, maxWidth: 400,
        }}>
          ⚠ {error}
        </div>
      )}
    </div>
  );
}

function PriceCard({ label, price, per, note, onClick, loading, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        padding: "16px 12px",
        background: C.purple + "15",
        border: `1px solid ${C.purple}`,
        borderRadius: 10,
        cursor: disabled ? "default" : "pointer",
        textAlign: "center",
        transition: "all 0.15s",
      }}
    >
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: C.purple, fontFamily: "monospace" }}>
        {price}
      </div>
      <div style={{ fontSize: 11, color: C.muted }}>{per}</div>
      <div style={{ fontSize: 10, color: C.purple, marginTop: 6 }}>
        {loading ? "Loading…" : note}
      </div>
    </button>
  );
}
